import Template from "../app/Template";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useSearchParams } from "react-router-dom";
import Button from "../layout/Button";
import Card from "../layout/Card";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import TextInputSubmitOnEnterLeadingIcon from "../forms/TextInputSubmitOnEnterLeadingIcon";
import SectionHeader from "../layout/SectionHeader";
import PricingCard from "../pricing/PricingCard";
import { useEffect, useState } from "react";
import { getCategorySearch } from "../../slices/category";
import { searchContent } from "../../slices/content";
import Spinner from "../layout/Spinner";
import ExploreTopicsAndCollections from "../layout/ExploreTopicsAndCollections";
import PostCard from "../layout/PostCard";
import { Media, Post } from "../../types/content";
import { consumptionFilter, withFilters } from "../../utils/searchParams";
import ConsumptionFilters from "../layout/ConsumptionFilters";


export default function Search() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams, setSearchParams] = useSearchParams();
  const querySearch = searchParams.get("query");
  const queryType = searchParams.get("type") || null;
  const queryTopic = searchParams.get("topic");
  const queryInterest = searchParams.get("interest");
  const queryAudience = searchParams.get("audience");
  const queryConsumption = searchParams.get("consumption");
  const queryTag = searchParams.get("tag");
  const queryPageNumber = searchParams.get("page") === null
    ? 1
    : parseInt(searchParams.get("page") as string);

  useEffect(() => {
    dispatch(searchContent({
      pageNumber: queryPageNumber,
      query: querySearch,
      contentType: queryType,
      consumption: queryConsumption,
      topic: queryTopic,
      tag: queryTag,
      audience: queryAudience,
    }));
    // dispatch(getTags({ query: null, pageNumber: 1, pageSize: 8 }));
  },
    [
      queryType,
      querySearch,
      queryTopic,
      queryInterest,
      queryAudience,
      queryConsumption,
      queryPageNumber,
      queryTag,
    ]
  );

  useEffect(() => {
    dispatch(getCategorySearch())
  }, []);

  // Get app level state from redux store
  const { user, loading: authLoading } = useAppSelector((state) => state.auth);
  const {
    search: { data: searchData, loading: searchLoading, waiting: searchWaiting, pagination: searchPagination },
    // tags: { data: tagsData, loading: tagsLoading },
  } = useAppSelector((state) => state.content);
  const {
    options: { data: optionsData, loading: optionsLoading }
  } = useAppSelector((state) => state.category);

  // Component level state.
  //
  // Seeded from the URL, not empty: /search?query=Anxiety used to render an empty
  // search box because this started at "" and nothing ever copied the query back in.
  const [searchState, setSearchState] = useState(querySearch ?? "");

  // Re-sync when the query changes without a keystroke — browser back/forward, and
  // the "Popular" buttons below, which set the URL directly.
  useEffect(() => {
    setSearchState(querySearch ?? "");
  }, [querySearch]);

  /**
   * Apply a change to the search filters. See `withFilters` for the rules; the
   * dropdowns each used to reset pagination by hand and the refine pills did not, so
   * routing every change through one place removes the chance to forget.
   *
   * @param changes value `null` removes the param.
   */
  const applyFilters = (changes: Record<string, string | null>) => {
    setSearchParams(withFilters(searchParams, changes));
  };

  // Constant variables
  let buttonFilters: any[] = [];
  // if (optionsData.presetsearch !== undefined) {
  //   optionsData.presetsearch.forEach((x: any) => {
  //     buttonFilters.push({
  //       type: "preset",
  //       value: x.name,
  //     });
  //   })
  // }
  if (optionsData.frequentlysearched !== undefined) {
    optionsData.frequentlysearched.forEach((x: any) => {
      buttonFilters.push({
        type: "frequentlysearched",
        value: x.name,
      });
    })
  }

  // Limit button filters to max 8 values
  if (buttonFilters.length > 8) {
    buttonFilters = buttonFilters.slice(0, 8);
  }

  return (
    <Template navbarMode="solid" footerBgColour="dark-beige">

      <div className="grid grid-col-1 gap-y-20 mb-20">
        {/* Hero */}
        <div>
          <div
            className="mt-24 sm:mt-28 md:mt-40 flex items-center justify-center w-4/5 mx-auto bg-off-white relative"
          >
            <Card colour="yellow" className="w-full grid grid-cols-1 lg:grid-cols-5 gap-y-8" paddingX="px-8 sm:px-16" paddingY="py-8 sm:py-16">

              {/* Left column */}
              <h1 className="col-span-2 text-dark-grey text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold">What are you interested in today?</h1>

              {/* Right column */}
              <div className="col-span-3 h-full grid grid-cols-1 justify-center items-center gap-y-4 px-4 my-auto">

                {/* Search input */}
                <TextInputSubmitOnEnterLeadingIcon
                  id="search-library"
                  placeholder="Search our library"
                  value={searchState}
                  icon={
                    <MagnifyingGlassIcon
                      aria-hidden="true"
                      className={`pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-dark-grey sm:size-4`}
                    />
                  }
                  widthClass="w-full"
                  onChange={(e) => setSearchState(e.target.value)}
                  onClick={() => {
                    // An empty box clears the search rather than doing nothing.
                    // The old `if (searchState !== "")` guard made submitting an
                    // empty box a no-op, so deleting the keyword left the previous
                    // results on screen with no way to reset short of editing the URL.
                    applyFilters({ query: searchState.trim() || null });
                  }}
                  loading={false}
                  shadow="md"
                />

                {/* Tag filter buttons.
                    Hidden entirely when there is nothing to show — the label used to
                    render unconditionally, so an empty FrequentlySearched table left
                    a dangling "Popular:" with no buttons after it. */}
                {(optionsLoading || buttonFilters.length > 0) && (
                <div className="flex flex-wrap gap-2 justify-center items-center px-8 md:px-10 lg:px-12 font-bold text-dark-grey">
                  Popular: {optionsLoading ? <Spinner size={8} /> : buttonFilters.map((obj, index) => {
                    return (
                      <Button
                        key={`filter-${index}`}
                        onClick={() => applyFilters({ query: obj.value })}
                        width="fit"
                        bgColour={querySearch !== obj.value ? `transparent hover:bg-dark-grey` : `dark-grey`}
                        borderColour="dark-grey"
                        textColour={querySearch !== obj.value ? `dark-grey hover:text-white` : `white`}
                      >
                        {obj.value}
                      </Button>
                    );
                  })}
                </div>
                )}
              </div>
            </Card>

            {/* Bubbles */}
            <img
              src="/img/graphics/bubbles/bubble-top-orange.svg"
              alt="Bubble"
              className="absolute w-20 lg:w-24 object-cover -top-4 -right-8 lg:-right-28 z-0"
            />
            <img
              src="/img/graphics/bubbles/bubble-right-blue.svg"
              alt="Bubble"
              className="absolute w-32 lg:w-48 object-cover top-10 -right-12 lg:-right-28 z-0"
            />
          </div>

          <div
            className="mt-8 grid grid-cols-1 items-center justify-center w-full mx-auto bg-off-white"
          >

            {/* Filters */}
            <div className="flex flex-row flex-wrap gap-4 justify-center items-center px-4 sm:px-0">
              <span className="font-semibold text-dark-grey text-base">Refine search</span>

              {/* Type */}
              {/* <Dropdown
                id="type-dropdown"
                className=""
                showLabel={false}
                required={false}
                onChange={(e) => {
                  if (e.target.value === "") {
                    searchParams.delete("type");
                    setSearchParams(searchParams);
                    return;
                  };
                  searchParams.delete("page");
                  const updatedSearchParams = searchParams;
                  searchParams.forEach((value: any, key: string) => {
                    updatedSearchParams.set(key, value);
                  })
                  updatedSearchParams.set("type", e.target.value);
                  setSearchParams(updatedSearchParams)
                }}
                value={queryType || ""}
              >
                <option key="" value="">
                  All
                </option>
                {dropdownTypes.map((opt, idx) => (
                  <option key={`${idx}-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Dropdown> */}

              {/* Topic */}
              {/* <Dropdown
                id="topic-dropdown"
                className=""
                showLabel={false}
                required={false}
                onChange={(e) => {
                  if (e.target.value === "") {
                    searchParams.delete("topic");
                    setSearchParams(searchParams);
                    return;
                  };
                  searchParams.delete("page");
                  const updatedSearchParams = searchParams;
                  searchParams.forEach((value: any, key: string) => {
                    updatedSearchParams.set(key, value);
                  })
                  updatedSearchParams.set("topic", e.target.value);
                  setSearchParams(updatedSearchParams)
                }}
                value={queryTopic || ""}
              >
                <option key="" value="">
                  All Topics
                </option>
                {dropdownTopics.map((opt, idx) => (
                  <option key={`${idx}-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Dropdown> */}

              {/* Audience */}
              {/* <Dropdown
                id="audience-dropdown"
                className=""
                showLabel={false}
                required={false}
                onChange={(e) => {
                  if (e.target.value === "") {
                    searchParams.delete("audience");
                    setSearchParams(searchParams);
                    return;
                  };
                  searchParams.delete("page");
                  const updatedSearchParams = searchParams;
                  searchParams.forEach((value: any, key: string) => {
                    updatedSearchParams.set(key, value);
                  })
                  updatedSearchParams.set("audience", e.target.value);
                  setSearchParams(updatedSearchParams)
                }}
                value={queryAudience || ""}
              >
                <option key="" value="">
                  All Audiences
                </option>
                {dropdownAudience.map((opt, idx) => (
                  <option key={`${idx}-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Dropdown> */}

              {/* Consumption */}
              {/* <Dropdown
                id="consumption-dropdown"
                className=""
                showLabel={false}
                required={false}
                onChange={(e) => {
                  if (e.target.value === "") {
                    searchParams.delete("consumption");
                    setSearchParams(searchParams);
                    return;
                  };
                  searchParams.delete("page");
                  const updatedSearchParams = searchParams;
                  searchParams.forEach((value: any, key: string) => {
                    updatedSearchParams.set(key, value);
                  })
                  updatedSearchParams.set("consumption", e.target.value);
                  setSearchParams(updatedSearchParams)
                }}
                value={queryConsumption || ""}
              >
                <option key="" value="">
                  All Content
                </option>
                {dropdownConsumption.map((opt, idx) => (
                  <option key={`${idx}-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Dropdown> */}
              <ConsumptionFilters
                selected={queryConsumption}
                onSelect={(consumption) => applyFilters(consumptionFilter(consumption))}
              />
            </div>

            {/* Content */}
            {searchLoading ? <div className="w-full flex justify-center mx-auto my-20"><Spinner size={8} /></div> : (
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-16 md:px-20 lg:px-32">
                {searchData.map((content: Post | Media, index) => {
                  let contentObj: any = null;
                  if (content.content_type === "Post") {
                    contentObj = (
                      <PostCard
                        key={`content-post-${index}`}
                        objectId={content.id}
                        objectType={content.content_type}
                        image={content.images.length === 0 ? "/img/graphics/hero/landing-hero.png" : content.images[0]}
                        bgColour="dark-beige"
                        href={content.url}
                        title={content.display_title}
                        authors={content.authors}
                        primaryTags={content.display_primary_tags}
                        synopsis={content.synopsis}
                        consumption={content.consumption}
                        user={user}
                        rating={content.rating}
                        reviews={content.reviews}
                        price={content.price}
                        locked={content.locked}
                      />
                    )
                  } else if (content.content_type === "Media") {
                    contentObj = (
                      <PostCard
                        key={`content-media-${index}`}
                        objectId={content.id}
                        objectType={content.content_type}
                        image={content.images.length === 0 ? "/img/graphics/hero/landing-hero.png" : content.images[0]}
                        bgColour="dark-beige"
                        href={content.url}
                        title={content.display_title}
                        authors={content.authors}
                        primaryTags={content.display_primary_tags}
                        synopsis={content.synopsis}
                        consumption={content.consumption}
                        user={user}
                        rating={content.rating}
                        reviews={content.reviews}
                        price={content.price}
                        locked={content.locked}
                      />
                    )
                  }
                  return contentObj;

                })}
              </div>
            )}

            {/* Pagination */}
            {searchPagination.has_next && (
              <div className="mt-8 flex items-center justify-center">
                {searchWaiting ? <Spinner size={6} /> : (
                  <Button
                    onClick={() => {
                      searchParams.set("page", (queryPageNumber + 1).toString());
                      setSearchParams(searchParams)
                    }}
                    width="fit"
                    bgColour="transparent hover:bg-dark-grey"
                    borderColour="dark-grey"
                    textColour="dark-grey hover:text-white"
                  >
                    Show more
                  </Button>
                )}
              </div>
            )}

          </div>
        </div>

        {/* You may also like */}
        <SectionHeader
          text="You may also like"
        />

        {/* Topics and collections */}
        <ExploreTopicsAndCollections />

        {/* Pricing */}
        <div className="w-2/3 mx-auto">
          <PricingCard />
        </div>

      </div>

    </Template>
  );
}
